import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import type { VocabWord } from 'modules/vocabulary';
import { LearnedToggle, StatusChip, sourceLabel } from './WordStatus';

/** The sentence the word was saved from, with the word itself marked. */
export function MarkedContext({ context, word, markClass = 'bg-[#FDE68A] dark:bg-amber-400/40' }: { context: string; word: string; markClass?: string }) {
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = context.split(new RegExp(`(${escaped})`, 'i'));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === word.toLowerCase() ? (
          <mark key={index} className={cn('rounded-[3px] px-0.5 text-foreground', markClass)}>{part}</mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

/** Grid shared with the header row so the columns line up. */
export const WORDS_GRID = 'md:grid md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,2fr)_minmax(0,0.9fr)_96px_80px] md:items-center md:gap-4';

interface WordRowProps {
  item: VocabWord;
  locked: boolean;
  onDelete: () => void;
  onToggleLearned: () => void;
}

export function WordRow({ item, locked, onDelete, onToggleLearned }: WordRowProps) {
  const { t, i18n } = useTranslation();
  const def = item.definition;
  const translation = def ? (i18n.language.startsWith('ru') ? def.ru : def.uz) : '';
  const example = def?.example || item.context;

  return (
    <li className={`relative flex flex-col gap-1.5 px-4 py-3.5 md:static md:px-[22px] ${WORDS_GRID}`}>
      <p className="flex items-baseline gap-x-2">
        <span className="font-display text-[17px] font-extrabold tracking-[-0.02em] text-foreground">{item.word}</span>
        {def?.partOfSpeech && <span className="text-xs italic text-muted-foreground">{def.partOfSpeech}</span>}
      </p>
      <div className="text-sm text-foreground">
        {def ? (
          <>
            {translation && <span className="font-medium">{translation}</span>}
            <span className="block text-[13px] text-muted-foreground">{def.meaning}</span>
          </>
        ) : locked ? (
          <span className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground">
            <Lock className="h-3.5 w-3.5" aria-hidden /> {t('vocab.definition_locked')}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </div>
      <p className="line-clamp-2 font-serif text-[13px] leading-normal text-muted-foreground">
        {example ? <MarkedContext context={example} word={item.word} /> : '—'}
      </p>
      <p className="text-[13px] text-muted-foreground">{sourceLabel(item) || '—'}</p>
      <div><StatusChip learned={item.learned === true} /></div>
      <div className="flex items-center justify-self-end max-md:absolute max-md:right-2 max-md:top-2">
        <LearnedToggle item={item} onToggle={onToggleLearned} variant="icon" />
        <Button type="button" variant="ghost" size="icon" onClick={onDelete} aria-label={t('vocab.delete', { word: item.word })}>
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </li>
  );
}
